/**
 * Redux slice for admin cron job settings and manual runs.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import type {
  JobInfo,
  JobName,
  JobListResponse,
  JobRunResponse,
} from '@/app/services/jobsService';
import {
  fetchJobs,
  patchJob,
  runJob,
  JobRunDisabledError,
} from '@/app/services/jobsService';

export interface JobsState {
  items: JobInfo[];
  total: number;
  loading: boolean;
  error: string | null;
  patchingJob: JobName | null;
  runningJob: JobName | null;
  lastRunResponse: JobRunResponse | null;
  lastRunError: {
    jobName: JobName;
    message: string;
    disabledReason: string | null;
  } | null;
}

const initialState: JobsState = {
  items: [],
  total: 0,
  loading: false,
  error: null,
  patchingJob: null,
  runningJob: null,
  lastRunResponse: null,
  lastRunError: null,
};

export const fetchJobsThunk = createAsyncThunk<
  JobListResponse,
  void,
  { state: RootState; rejectValue: string }
>('jobs/fetch', async (_, { rejectWithValue }) => {
  try {
    return await fetchJobs();
  } catch (error: unknown) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch jobs'
    );
  }
});

export const patchJobThunk = createAsyncThunk<
  JobInfo,
  { jobName: JobName; enabled: boolean; reason: string },
  { state: RootState; rejectValue: string }
>('jobs/patch', async ({ jobName, enabled, reason }, { rejectWithValue }) => {
  try {
    return await patchJob(jobName, { enabled, reason });
  } catch (error: unknown) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to update job'
    );
  }
});

export type RunJobRejectedPayload = {
  jobName: JobName;
  message: string;
  disabledReason: string | null;
};

export const runJobThunk = createAsyncThunk<
  JobRunResponse,
  { jobName: JobName; dryRun: boolean },
  { state: RootState; rejectValue: RunJobRejectedPayload }
>('jobs/run', async ({ jobName, dryRun }, { rejectWithValue }) => {
  try {
    return await runJob(jobName, { dry_run: dryRun });
  } catch (error: unknown) {
    if (error instanceof JobRunDisabledError) {
      return rejectWithValue({
        jobName,
        message: error.message,
        disabledReason: error.disabledReason,
      });
    }
    const message =
      error instanceof Error ? error.message : 'Failed to run job';
    return rejectWithValue({
      jobName,
      message,
      disabledReason: null,
    });
  }
});

const jobsSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    clearLastRun(state) {
      state.lastRunResponse = null;
      state.lastRunError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.jobs.slice();
        state.total = action.payload.total;
      })
      .addCase(fetchJobsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      })
      .addCase(patchJobThunk.pending, (state, action) => {
        state.patchingJob = action.meta.arg.jobName;
        state.error = null;
      })
      .addCase(patchJobThunk.fulfilled, (state, action) => {
        state.patchingJob = null;
        const idx = state.items.findIndex(
          (j) => j.job_name === action.payload.job_name
        );
        if (idx !== -1) {
          state.items[idx] = action.payload;
        }
      })
      .addCase(patchJobThunk.rejected, (state, action) => {
        state.patchingJob = null;
        state.error = action.payload ?? 'Unknown error';
      })
      .addCase(runJobThunk.pending, (state, action) => {
        state.runningJob = action.meta.arg.jobName;
        state.lastRunResponse = null;
        state.lastRunError = null;
      })
      .addCase(runJobThunk.fulfilled, (state, action) => {
        state.runningJob = null;
        state.lastRunResponse = action.payload;
        state.lastRunError = null;
      })
      .addCase(runJobThunk.rejected, (state, action) => {
        state.runningJob = null;
        if (action.payload != null) {
          state.lastRunError = {
            jobName: action.payload.jobName,
            message: action.payload.message,
            disabledReason: action.payload.disabledReason,
          };
        } else {
          const { jobName } = action.meta.arg;
          state.lastRunError = {
            jobName,
            message: action.error.message ?? 'Failed to run job',
            disabledReason: null,
          };
        }
      });
  },
});

export const { clearLastRun } = jobsSlice.actions;

export const selectAllJobs = (state: RootState): JobInfo[] => state.jobs.items;

export const selectJobByName =
  (jobName: JobName) =>
  (state: RootState): JobInfo | undefined =>
    state.jobs.items.find((j) => j.job_name === jobName);

export const selectJobsLoading = (state: RootState): boolean => state.jobs.loading;

export const selectJobsError = (state: RootState): string | null =>
  state.jobs.error;

export const selectLastRunResponse = (state: RootState): JobRunResponse | null =>
  state.jobs.lastRunResponse;

export const selectLastRunError = (state: RootState): JobsState['lastRunError'] =>
  state.jobs.lastRunError;

export const selectPatchingJob = (state: RootState): JobName | null =>
  state.jobs.patchingJob;

export const selectRunningJob = (state: RootState): JobName | null =>
  state.jobs.runningJob;

export default jobsSlice.reducer;
